/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';

export type StaticGridConfig = {
  rowBuffer: number
}

// defines the application wide static grid configuration.
let configuration: StaticGridConfig = null;

/**
 * The `setStaticGridConfig` function allows for providing application wide static
 * grid configurations that are to be used by all `IonGrid` instances within the
 * application. The values provided to the configuration will serve as the defaults
 * returned by the `useStaticGridConfig` hook, and can be overridden on a per grid
 * basis via the arguments provided to the hook. If no arguments are provided, then
 * the default configuration will be used.
 *
 * ```typescript
 *  // _app.tsx
 *  import {setStaticGridConfig} from '@ion/dls/src/grid/useStaticGridConfig';
 *  ...
 *  const App = ({Component, pageProps}: ApplicationPage) => {
 *    ...
 *    useEffect(() => {
 *       // set all IonGrid instances to use a rowBuffer of 50
 *       setStaticGridConfig({rowBuffer: 50});
 *    }, []);
 *    ...
 *  }
 * ```
 */
export const setStaticGridConfig = (config: StaticGridConfig) => {
  if (!configuration) {
    configuration = config;
  }
};

/**
 * The `useStaticGridConfig` hook provides access to the application-wide static
 * grid configurations which are to be used by all `IonGrid` instances within an
 * application. The arguments provided can be used to override the default static
 * configuration on a per grid basis as needed. If no arguments are provided, then
 * the default configuration will be used.
 *
 * Note: This hook is generally intended for use by the `IonGrid` component.
 *
 * @param   rowBuffer The `IonGrid` optional `rowBuffer` prop, if provided.
 * @param   defaultRowBuffer A default `rowBuffer` value to be used if not provided
 *          to the `setStaticGridConfig` call.
 * @returns The grid configuration based on the statically defined values, the
 *          provided arguments, or a combination of both.
 */
export const useStaticGridConfig = (rowBuffer?: number, defaultRowBuffer = 90) => {
  const config = useMemo(() => {
    const cfg = {...configuration};
    if (!isNaN(rowBuffer)) {
      cfg.rowBuffer = rowBuffer;
    }
    if (isNaN(cfg.rowBuffer)) {
      cfg.rowBuffer = defaultRowBuffer;
    }
    return cfg;
  }, [rowBuffer, defaultRowBuffer]);

  return config;
};
